import { useLatest } from 'ahooks';
import type { RefObject } from 'react';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useEventListener } from '../useEventListener';

export interface Options {
    ref?: RefObject<HTMLIFrameElement | null>;
    id?: string;
}

type MessageDispatcher<Message> = (message: Message) => void;
type UseIframeResult<Message> = [MessageDispatcher<Message>];

export type MessageHandler<Message> = (
    message: Message,
    dispatch: MessageDispatcher<Message>,
) => void;

interface PrivateMessage {
    __private: true;
    type: 'mounted';
    id: string;
}

const packetId = '__fromUseIframeHook';

const encodeMessage = <Message>(message: Message, fromId?: string) =>
    JSON.stringify({
        fromId,
        payload: JSON.stringify(message),
        [packetId]: true,
    });

const decodeMessage = <Message>(
    // eslint-disable-next-line
    raw: any,
): [Message, string | undefined] | undefined => {
    try {
        const decoded = JSON.parse(raw);
        if (packetId in decoded && decoded[packetId] === true) {
            const message = JSON.parse(decoded.payload) as Message;
            return [message, decoded.fromId];
        }
    } catch {
        return undefined;
    }
    return undefined;
};

const isPrivateMessage = (
    // eslint-disable-next-line
    message: any,
): message is PrivateMessage =>
    // eslint-disable-next-line
    message.hasOwnProperty('__private') &&
    '__private' in message &&
    message.__private === true;

const resolveChildIframeId = (): string | undefined => {
    if (typeof window === 'undefined' || window === window.top) {
        return undefined;
    }

    const iframe = Array.from(
        window.parent.document.getElementsByTagName('iframe'),
    ).find((element) => element.contentWindow === window);

    return iframe?.id;
};

// eslint-disable-next-line
const useIframe = <Message = any>(
    messageHandler: MessageHandler<Message>,
    options?: Options,
): UseIframeResult<Message> => {
    const ref = options?.ref;
    const handleMessage = useLatest(messageHandler);

    const isParent = useMemo(() => ref !== undefined, [ref]);
    const isChild = !isParent && window !== window.top;

    /** Scope: Parent */
    const iframeId = ref?.current?.id;
    const [childId, setChildId] = useState<string | undefined>();

    const [queue, setQueue] = useState<(Message | PrivateMessage)[]>([]);

    /** Scope Parent */
    const isChildMounted = !!options?.id || childId !== undefined;

    const dispatch = useCallback(
        (message: Message) => {
            if (isParent || isChild) {
                setQueue((q) => [...q, message]);
            }
        },
        [isParent, isChild],
    );

    const dispatchPrivate = useCallback(
        (message: PrivateMessage) => {
            if (isParent || isChild) {
                setQueue((q) => [...q, message]);
            }
        },
        [isParent, isChild],
    );

    const handlePrivateMessage = useCallback(
        (message: PrivateMessage, event: MessageEvent) => {
            switch (message.type) {
                case 'mounted':
                    if (
                        ref?.current?.id === message.id ||
                        iframeId === message.id ||
                        ref?.current?.contentWindow === event.source
                    ) {
                        setChildId(message.id);
                    }
            }
        },
        [iframeId, ref],
    );

    const postMessage = useCallback(
        (message: Message | PrivateMessage) => {
            if (isParent && ref?.current) {
                ref?.current?.contentWindow?.postMessage(
                    encodeMessage(message),
                    '*',
                );
            } else if (isParent === false) {
                window.parent.postMessage(
                    encodeMessage(message, resolveChildIframeId() ?? childId),
                    '*',
                );
            }
        },
        [isParent, childId, ref],
    );

    const isChildReachable =
        isChildMounted || ref?.current?.contentWindow != null;

    useEffect(() => {
        const message = queue[0];
        if (isParent) {
            if (message && isChildReachable) {
                setQueue((q) => q.slice(1));
                postMessage(message);
            }
        } else {
            if (message) {
                setQueue((q) => q.slice(1));
                postMessage(message);
            }
        }
    }, [isParent, isChildReachable, postMessage, queue]);

    const eventListener = useCallback(
        (event: MessageEvent) => {
            const [message, fromId] =
                decodeMessage<Message | PrivateMessage>(event.data) || [];
            if (message) {
                if (isPrivateMessage(message)) {
                    handlePrivateMessage(message, event);
                } else {
                    if (isParent) {
                        const iframeWindow = ref?.current?.contentWindow;
                        const isFromOurIframe =
                            iframeWindow != null &&
                            event.source === iframeWindow;
                        const isFromOurIframeById =
                            iframeId != null &&
                            iframeId !== '' &&
                            iframeId === fromId;

                        if (isFromOurIframe || isFromOurIframeById) {
                            if (!childId && ref?.current?.id) {
                                setChildId(ref.current.id);
                            }
                            handleMessage.current(message, dispatch);
                        }
                    } else {
                        handleMessage.current(message, dispatch);
                    }
                }
            }
        },
        [
            handlePrivateMessage,
            isParent,
            iframeId,
            handleMessage,
            dispatch,
            ref,
            childId,
        ],
    );

    useEventListener(eventListener);

    useEffect(() => {
        if (!isParent) {
            const iframes = Array.from(
                window.parent.document.getElementsByTagName('iframe'),
            );
            const iframe = iframes.find(
                (iframe) => iframe.contentWindow === window,
            );
            if (iframe) {
                dispatchPrivate({
                    type: 'mounted',
                    id: iframe.id,
                    __private: true,
                });
                setChildId(iframe.id);
            }
        }
    }, [dispatchPrivate, isParent]);

    return [dispatch];
};

export default useIframe;
